import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  takeUntil,
  map,
  startWith,
  pairwise,
  filter,
  switchMap,
  take,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { RequestsFacade } from '../../../store/requests/requests.facade';
import { RequestDetailPageActions } from '../../../store/requests/requests.actions';
import { LoggingService } from '../../../core/services/logging.service';
import { AuthService } from '../../../core/services/auth.service';
import { RequestDetail } from '../../../core/models/request-detail.model';
import { FileDetail } from '../../../core/models/file-detail.model';
import { UserListItem } from '../../../core/models/user-list.model';
import { RequestStatusOption } from '../../../core/models/request-status-option.model';
import { PrintSiteOption } from '../../../core/models/print-site-option.model';
import { PrintLocationDetailOption } from '../../../core/models/print-location-option.model';
import { FileDetailsGridComponent } from './components/file-details-grid/file-details-grid.component';
import { ShippingDetailsModalComponent } from './components/shipping-details-modal/shipping-details-modal.component';
import { RequestStatus } from '../../../core/enums/request-status.enum';
import {
  ProcessRequestForUluroRequest,
  ProcessRequestForUluroResult,
} from '../../../core/models/process-request.model';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permissions } from '../../../core/enums';

@Component({
  selector: 'app-request-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FileDetailsGridComponent,
    ShippingDetailsModalComponent,
    HasPermissionDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './request-details.component.html',
  styleUrls: ['./request-details.component.scss'],
})
export class RequestDetailsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly facade = inject(RequestsFacade);
  private readonly fb = inject(FormBuilder);
  private readonly logger = inject(LoggingService);
  public readonly authService = inject(AuthService);
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  public readonly Permissions = Permissions;

  readonly request$ = this.facade.currentRequest$;
  readonly loading$ = this.facade.currentRequestLoading$;
  private readonly localError$ = new BehaviorSubject<string | null>(null);
  readonly error$ = combineLatest([
    this.facade.currentRequestError$,
    this.localError$,
  ]).pipe(map(([serviceError, localError]) => localError ?? serviceError));

  // Combine user list with assigned user to ensure assigned user is always in dropdown
  readonly userListForDropdown$: Observable<UserListItem[]> = combineLatest([
    this.facade.userList$.pipe(startWith([])),
    this.request$.pipe(startWith(null)),
  ]).pipe(
    map(([userList, request]) => {
      if (!request?.assignedUserId || !request?.assignedTo) {
        return userList;
      }

      // Check if assigned user is already in the list
      const userExists = userList.some(
        user => user.id === request.assignedUserId
      );

      if (userExists) {
        return userList;
      }

      // Add assigned user to the list if not present (inactive user)
      const assignedUser: UserListItem = {
        id: request.assignedUserId,
        userName: request.assignedTo,
      };

      return [assignedUser, ...userList];
    })
  );

  // Combine request statuses with current status to ensure current status is always in dropdown
  readonly requestStatusesForDropdown$: Observable<RequestStatusOption[]> =
    combineLatest([
      this.facade.requestStatuses$.pipe(startWith([])),
      this.request$.pipe(startWith(null)),
    ]).pipe(
      map(([statuses, request]) => {
        if (!request?.requestStatusId) {
          return statuses;
        }

        // Check if current status is already in the list
        const statusExists = statuses.some(
          s => s.id === request.requestStatusId
        );

        if (statusExists) {
          return statuses;
        }

        // Add current status if not in active list
        const currentStatus: RequestStatusOption = {
          id: request.requestStatusId,
          status: request.requestStatus?.toString() ?? 'Unknown',
          displayOrder: 999,
          isActive: false,
        };

        return [...statuses, currentStatus];
      })
    );

  // Combine print sites with current site to ensure current site is always in dropdown
  readonly printSitesForDropdown$: Observable<PrintSiteOption[]> =
    combineLatest([
      this.facade.printSites$.pipe(startWith([])),
      this.request$.pipe(startWith(null)),
    ]).pipe(
      map(([sites, request]) => {
        if (!request?.printSiteId) {
          return sites;
        }

        // Check if current print site is already in the list
        const siteExists = sites.some(s => s.id === request.printSiteId);

        if (siteExists) {
          return sites;
        }

        // Add current print site if not in active list
        const currentSite: PrintSiteOption = {
          id: request.printSiteId,
          printSiteName: request.printSite?.toString() ?? 'Unknown',
          isActive: false,
        };

        return [...sites, currentSite];
      })
    );

  // Combine print locations with current location to ensure current location is always in dropdown
  readonly printLocationsForDropdown$: Observable<PrintLocationDetailOption[]> =
    combineLatest([
      this.facade.printLocationsBySite$.pipe(startWith([])),
      this.request$.pipe(startWith(null)),
    ]).pipe(
      map(([locations, request]) => {
        if (!request?.printLocationId) {
          return locations;
        }

        // Check if current print location is already in the list
        const locationExists = locations.some(
          l => l.id === request.printLocationId
        );

        if (locationExists) {
          return locations;
        }

        const savedPrintSiteId = request.printSiteId ?? 0;

        const isSameSite =
          locations.length > 0 &&
          locations.some(l => l.printSiteId === savedPrintSiteId);

        if (!isSameSite) {
          return locations;
        }

        const currentLocation: PrintLocationDetailOption = {
          id: request.printLocationId,
          locationName: request.printLocationName ?? 'Unknown',
          printSiteId: savedPrintSiteId,
          address: null,
          city: null,
          state: null,
          zipCode: null,
          isActive: false,
        };

        return [...locations, currentLocation];
      })
    );

  readonly processRequestLoading$ = this.facade.processRequestLoading$;
  readonly processRequestError$ = this.facade.processRequestError$;

  detailForm!: FormGroup;
  requestNumber: number | null = null;
  currentUserEmail: string | null = null;
  currentUserId: number | null = null;
  currentRequest: RequestDetail | null = null; // Cache current request to avoid re-subscribing

  // Action button state
  isShippingModalOpen = false;
  canProcessRequest = false;
  successMessage: string | null = null;
  processWarning: string | null = null;

  ngOnInit(): void {
    this.initForm();

    // Subscribe to current user to get email
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUserEmail = user?.email ?? null;
        this.currentUserId = user?.id ?? null;
        this.logger.debug('Current user email retrieved', {
          email: this.currentUserEmail,
        });
      });

    // Listen for successful process result (process API is now called directly from this page)
    this.facade.processRequestResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.onProcessSuccess(result);
        }
      });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const requestNumberParam = params['requestNumber'];
      if (requestNumberParam) {
        // Extract numeric ID from request number (e.g., "REQ-002619" -> 2619)
        let numericId: number;
        if (requestNumberParam.startsWith('REQ-')) {
          numericId = parseInt(requestNumberParam.substring(4), 10);
        } else {
          numericId = parseInt(requestNumberParam, 10);
        }

        if (!isNaN(numericId)) {
          this.requestNumber = numericId;
          this.localError$.next(null);
          this.logger.debug('Loading request details', {
            requestNumber: this.requestNumber,
          });
          this.facade.loadRequestDetail(this.requestNumber);
        } else {
          this.logger.error('Invalid request number', undefined, {
            requestNumberParam,
          });
          this.localError$.next(
            `Invalid request number: ${requestNumberParam}`
          );
        }
      } else {
        this.logger.error('Missing request number in URL');
        this.localError$.next('Request number is required');
      }
    });

    // Subscribe to request changes to populate form
    let dropdownsLoaded = false;
    this.request$.pipe(takeUntil(this.destroy$)).subscribe(request => {
      if (request) {
        this.currentRequest = request; // Cache for comparison during updates
        this.populateForm(request);
        // Check if request can be processed
        // Request must be in Open status (statusId = 1 or enum Open = 6) and have files
        this.canProcessRequest =
          (request.requestStatus === RequestStatus.Open ||
            request.requestStatus === RequestStatus.Pending) &&
          request.fileDetails.length > 0 &&
          request.fileDetails.some(f => f.status === RequestStatus.Open);

        // Load print locations if print site is set
        if (request.printSiteId) {
          this.facade.loadPrintLocationsBySite(request.printSiteId);
        }

        // Load dropdown data in background after request loads (non-blocking)
        if (!dropdownsLoaded) {
          dropdownsLoaded = true;
          this.facade.loadUserList();
          this.facade.loadRequestStatuses();
          this.facade.loadPrintSites();
        }
      }
    });

    // Watch for print site changes to load corresponding print locations
    this.detailForm
      .get('printSiteId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((printSiteId: number | null) => {
        if (printSiteId) {
          this.logger.debug('Print site changed, loading print locations', {
            printSiteId,
          });
          this.facade.loadPrintLocationsBySite(printSiteId);
          // Reset print location when site changes
          this.detailForm.patchValue({ printLocationId: null });
        } else {
          // Clear print locations when no site selected
          this.detailForm.patchValue({ printLocationId: null });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.localError$.complete();
    this.facade.clearCurrentRequest();
  }

  private initForm(): void {
    this.detailForm = this.fb.group({
      // Contact Information
      companyName: [''],
      contactName: [''],
      phoneNumber: [''],
      emailAddresses: [''],

      // Processing Options
      expeditedRequest: [false],
      waiveExpeditedFee: [false],
      specialHandling: [false],
      printPostage: [false],

      // Printing Configuration
      printSiteId: [null as number | null],
      printLocationId: [null as number | null],

      // Workflow Tracking
      requestStatusId: [null as number | null],
      assignedUserId: [null as number | null],

      // Comments
      comments: [''],
      commentsHistory: [''],
    });
  }

  private populateForm(request: RequestDetail): void {
    this.detailForm.patchValue(
      {
        companyName: request.companyName ?? '',
        contactName: request.contactName ?? '',
        phoneNumber: request.phoneNumber ?? '',
        emailAddresses: request.emailAddresses ?? '',
        expeditedRequest: request.expeditedRequest ?? false,
        waiveExpeditedFee: request.waiveExpeditedFee ?? false,
        specialHandling: request.specialHandling ?? false,
        printPostage: request.printPostage ?? false,
        printSiteId: request.printSiteId,
        printLocationId: request.printLocationId,
        requestStatusId: request.requestStatusId,
        assignedUserId: request.assignedUserId,
        assignedByUserId: this.currentUserId ?? 0,
        comments: request.comments ?? '',
        commentsHistory: request.commentsHistory ?? '',
      },
      { emitEvent: false }
    );
  }

  goBack(): void {
    this.logger.debug('Navigating back to requests list');
    this.router.navigate(['/requests']);
  }

  retry(): void {
    if (this.requestNumber) {
      this.logger.debug('Retrying load request', {
        requestNumber: this.requestNumber,
      });
      this.facade.loadRequestDetail(this.requestNumber);
    }
  }

  /**
   * Helper to display field values with fallback for null/empty
   */
  displayValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return String(value);
  }

  // Action button handlers

  /**
   * Update Request: dispatch the update, and only once it has settled
   * (success or failure) do we refetch the request so the page never
   * shows data from a GET that raced ahead of the update.
   */
  onUpdateRequest(): void {
    if (!this.requestNumber || !this.currentRequest) {
      this.logger.error(
        'Cannot update request: No request number or request data'
      );
      return;
    }

    const updates = this.buildUpdatesIfChanged();

    if (!updates) {
      this.logger.debug('No changes detected, skipping update');
      this.showSuccessMessage('No changes to save');
      return;
    }

    const requestNumber = this.requestNumber;

    this.waitForSettled(
      this.facade.updateRequestLoading$,
      this.facade.updateRequestError$
    ).subscribe(succeeded => {
      if (succeeded) {
        this.facade.loadRequestDetail(requestNumber);
        this.showSuccessMessage('Request updated successfully');
      } else {
        this.processWarning = 'Failed to update request. Please try again.';
      }
      this.cdr.markForCheck();
    });

    this.logger.debug('Fields changed, updating request', { updates });
    this.facade.updateRequest(
      requestNumber,
      updates,
      this.currentUserEmail ?? 'unknown.user@fps.com'
    );
  }

  /**
   * Diffs the form against the current request and builds the update
   * payload. Returns null when nothing changed, so callers can decide
   * whether an update call is needed at all.
   */
  private buildUpdatesIfChanged():
    Parameters<RequestsFacade['updateRequest']>[1] | null {
    if (!this.requestNumber || !this.currentRequest) {
      return null;
    }

    const formValue = this.detailForm.value;
    const request = this.currentRequest;

    // Check if any fields changed (including status and assigned user)
    const fieldsChanged =
      formValue.contactName !== request.contactName ||
      formValue.phoneNumber !== request.phoneNumber ||
      formValue.emailAddresses !== request.emailAddresses ||
      formValue.expeditedRequest !== request.expeditedRequest ||
      formValue.waiveExpeditedFee !== request.waiveExpeditedFee ||
      formValue.specialHandling !== request.specialHandling ||
      formValue.printSiteId !== request.printSiteId ||
      formValue.requestStatusId !== request.requestStatusId ||
      formValue.assignedUserId !== request.assignedUserId ||
      formValue.comments !== request.comments;

    if (!fieldsChanged) {
      return null;
    }

    return {
      // Contact Information
      contactName: formValue.contactName ?? null,
      contactPhone: formValue.phoneNumber?.toString() ?? null,
      contactEmails: formValue.emailAddresses ?? null,

      // Processing Options
      sla: formValue.expeditedRequest ?? null,
      isSlaFeeWaived: formValue.waiveExpeditedFee ?? null,
      isSpecialHandling: formValue.specialHandling ?? null,

      // Print configuration (printLocationId is only set during request processing, not in updates)
      printSiteId: formValue.printSiteId,

      // Workflow Tracking
      requestStatusId: formValue.requestStatusId ?? null,
      assignedUserId: formValue.assignedUserId ?? null,
      assignedByUserId: this.currentUserId ?? 0,

      // Comments (send only if not empty)
      newComments: formValue.comments?.trim() ?? null,
    };
  }

  /**
   * Process Request: the update must complete successfully before we
   * process, and the process call must complete before we refetch —
   * otherwise the GET can land between the two writes and show stale data.
   */
  onProcessRequest(): void {
    if (!this.requestNumber || !this.currentRequest) {
      this.logger.error(
        'Cannot process request: No request number or request data'
      );
      return;
    }

    this.processWarning = null;

    if (!this.canProcessRequest) {
      this.processWarning =
        'This request cannot be processed. It must be in Open or Pending status with at least one file.';
      return;
    }

    const formValue = this.detailForm.value;

    if (!formValue.printLocationId) {
      this.detailForm.get('printLocationId')?.markAsTouched();
      this.processWarning =
        'Print Location is required to process this request.';
      return;
    }

    const requestNumber = this.requestNumber;
    const processRequestPayload: ProcessRequestForUluroRequest = {
      printLocationId: formValue.printLocationId,
      printPostage: formValue.printPostage ?? false,
      printerType: 'HP',
      sortOrder: 'Backwards',
      processingUserId: this.currentUserId ?? 0,
      modifiedBy: this.currentUserEmail ?? 'unknown.user@fps.com',
    };

    const updates = this.buildUpdatesIfChanged();

    if (!updates) {
      this.dispatchProcessRequest(requestNumber, processRequestPayload);
      return;
    }

    this.waitForSettled(
      this.facade.updateRequestLoading$,
      this.facade.updateRequestError$
    ).subscribe(succeeded => {
      if (succeeded) {
        this.dispatchProcessRequest(requestNumber, processRequestPayload);
      } else {
        this.processWarning =
          'Failed to save changes before processing. Please try again.';
      }
      this.cdr.markForCheck();
    });

    this.logger.debug('Fields changed, updating request before processing', {
      updates,
    });
    this.facade.updateRequest(
      requestNumber,
      updates,
      this.currentUserEmail ?? 'unknown.user@fps.com'
    );
  }

  private dispatchProcessRequest(
    requestNumber: number,
    request: ProcessRequestForUluroRequest
  ): void {
    this.waitForSettled(
      this.facade.processRequestLoading$,
      this.facade.processRequestError$
    ).subscribe(succeeded => {
      if (succeeded) {
        this.facade.loadRequestDetail(requestNumber);
      }
    });

    this.logger.debug('Processing request', { requestNumber, request });
    this.facade.processRequest(requestNumber, request);
  }

  /**
   * Resolves once a `loading$/error$` pair transitions from loading to
   * settled, emitting whether it settled without an error. Must be
   * subscribed to BEFORE dispatching the triggering action so the
   * true -> false loading transition isn't missed.
   */
  private waitForSettled(
    loading$: Observable<boolean>,
    error$: Observable<string | null>
  ): Observable<boolean> {
    return loading$.pipe(
      pairwise(),
      filter(([wasLoading, isLoading]) => wasLoading && !isLoading),
      take(1),
      switchMap(() => error$.pipe(take(1))),
      map(error => error === null),
      takeUntil(this.destroy$)
    );
  }

  private showSuccessMessage(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = null;
      this.cdr.markForCheck();
    }, 3000);
  }

  onProcessSuccess(result: ProcessRequestForUluroResult): void {
    this.logger.debug('Process request succeeded', {
      transactionsCreated: result.transactionsCreated,
      isOutsourcing: result.isOutsourcing,
    });

    // Show success message
    if (result.isOutsourcing) {
      this.successMessage = `Request sent to external vendor. ${result.transactionsCreated} transaction(s) created.`;
    } else {
      this.successMessage = `Request queued for printing. ${result.transactionsCreated} transaction(s) created.`;
    }

    // Clear success message after 3 seconds and reset store state
    setTimeout(() => {
      this.successMessage = null;
      this.cdr.markForCheck();
      // Dispatch clearProcessResult to clear the result from store so it doesn't trigger again
      this.store.dispatch(RequestDetailPageActions.clearProcessResult());
    }, 3000);
  }

  openShippingDetails(): void {
    this.isShippingModalOpen = true;
  }

  closeShippingDetails(): void {
    this.isShippingModalOpen = false;
  }

  onFileRowClicked(file: FileDetail): void {
    if (
      !this.authService.hasPermissionSync([Permissions.AccessToFileDetails])
    ) {
      return;
    }
    this.logger.debug('File row clicked, navigating to file details', {
      fileId: file.id,
      fileName: file.fileName,
    });
    this.router.navigate(['/file-details'], {
      queryParams: { id: file.id },
    });
  }

  get canOpenFileDetails(): boolean {
    return this.authService.hasPermissionSync([
      Permissions.AccessToFileDetails,
    ]);
  }

  get showReadyToProcess(): boolean {
    return !this.authService.hasPermissionSync([
      Permissions.AccessCustomersRequestsList,
    ]);
  }

  get canOpenShippingDetails(): boolean {
    return this.currentRequest?.requestStatus === RequestStatus.Completed;
  }
}
